import { apiUrl } from "../../utils/api";

/**
 * Typisierte Read-Fetcher für die Profil-Stammdaten (read-only, E4).
 *
 * Genutzte, bereits existierende Endpunkte:
 *   - `GET /telephony/me`  → Durchwahl/Nebenstelle (404/403 → null)
 *   - `GET /mail/mailboxes` → zugewiesene Postfächer
 *
 * Teams kommen NICHT über einen eigenen Fetcher: `useAuth().teams` ist bereits
 * aus `GET /rbac/me/permissions` befüllt (AuthContext). Ein separater
 * `fetchMyTeams` wäre toter Code (Konsistenz-Fix P8) → bewusst weggelassen.
 *
 * Der Bearer-Token wird global durch die gepatchte `fetch`-Implementierung
 * (`utils/api.ts`) gesetzt — hier kein manuelles Header-Handling nötig.
 */

/** FE-Mirror von `MyExtensionDto` (GET /telephony/me). */
export interface MyExtension {
  extension: string;
  displayName: string | null;
  email: string | null;
}

/** FE-Mirror von `MailboxListItem` (GET /mail/mailboxes). */
export interface MyMailbox {
  id: string;
  mailboxAddress: string;
  displayName: string | null;
  mailAccess: string;
  calendarAccess: string;
  canCreateTeamsMeetings: boolean;
  mailboxType: string;
}

/**
 * Durchwahl/Nebenstelle des eingeloggten Users.
 * 404 (kein Mapping) oder 403 (kein `calls:read`-Scope) → `null` (Anzeige „—").
 * Wirft nur bei echten Server-Fehlern.
 */
export async function fetchMyExtension(
  signal?: AbortSignal,
): Promise<MyExtension | null> {
  const res = await fetch(apiUrl("telephony/me"), { signal });
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) throw new Error(`telephony/me: ${res.status} ${res.statusText}`);
  return (await res.json()) as MyExtension;
}

/**
 * Dem User zugewiesene Postfächer. Wirft bei Fehlern, damit die Sektion einen
 * Lade-Fehlerzustand zeigen kann.
 */
export async function fetchMyMailboxes(
  signal?: AbortSignal,
): Promise<MyMailbox[]> {
  const res = await fetch(apiUrl("mail/mailboxes"), { signal });
  if (!res.ok) throw new Error(`mail/mailboxes: ${res.status} ${res.statusText}`);
  const json = await res.json();
  return Array.isArray(json) ? (json as MyMailbox[]) : [];
}
